import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableCell,
  TableRow,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../config/api';
import DataTable from '../components/DataTable';

function WalletTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ 
    today_credit: 0, 
    today_used: 0, 
    total_credit: 0, 
    total_debit: 0 
  });
  
  // Filter states
  const [filterTrainer, setFilterTrainer] = useState('');
  const [filterMobile, setFilterMobile] = useState('');
  const [filterOrderId, setFilterOrderId] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterTransactionType, setFilterTransactionType] = useState('');
  const [filterDeviceType, setFilterDeviceType] = useState('');
  const [filterPricePlan, setFilterPricePlan] = useState('');
  const [pricePlans, setPricePlans] = useState([]);

  const [formData, setFormData] = useState({
    trainer_id: '',
    transaction_type: 'credit',
    amount: '',
    description: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
    fetchTrainers();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterTrainer) params.append('trainer_id', filterTrainer);
      if (filterMobile) params.append('mobile', filterMobile);
      if (filterOrderId) params.append('order_id', filterOrderId);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterTransactionType) params.append('transaction_type', filterTransactionType);
      if (filterDeviceType) params.append('device_type', filterDeviceType);
      if (filterPricePlan) params.append('price_id', filterPricePlan);
      
      const response = await api.get(`/wallet/transactions?${params.toString()}`);
      setTransactions(response.data.data || []);
      setSummary(response.data.summary || { 
        today_credit: 0, 
        today_used: 0, 
        total_credit: 0, 
        total_debit: 0 
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filterTrainer, filterMobile, filterOrderId, filterStartDate, filterEndDate, filterTransactionType, filterDeviceType, filterPricePlan]);

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const fetchPricePlans = async () => {
    try {
      const url = filterDeviceType ? `/prices?device_type=${filterDeviceType}` : '/prices';
      const response = await api.get(url);
      setPricePlans(response.data.data || []);
    } catch (error) {
      console.error('Error fetching price plans:', error);
      setPricePlans([]);
    }
  };

  useEffect(() => {
    fetchPricePlans();
  }, [filterDeviceType]);

  const purchaseOrderOptions = useMemo(() => {
    const orders = transactions.map((tx) => tx.order_id).filter(Boolean);
    return [...new Set(orders)];
  }, [transactions]);

  const handleOpen = () => {
    setFormData({
      trainer_id: '',
      transaction_type: 'credit',
      amount: '',
      description: '',
    });
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      await api.post('/wallet/transactions', formData);
      handleClose();
      fetchTransactions();
    } catch (error) {
      setError(error.response?.data?.error || 'Error creating transaction');
    }
  };

  const handleClearFilters = () => {
    setFilterTrainer('');
    setFilterMobile('');
    setFilterOrderId('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterTransactionType('');
    setFilterDeviceType('');
    setFilterPricePlan('');
  };

  const handleUpdateRazorpayStatus = async (orderId) => {
    if (!window.confirm(`Are you sure you want to update this pending payment to success and credit coins?`)) {
      return;
    }
    
    try {
      const response = await api.put('/wallet/razorpay/update-status', {
        razorpay_order_id: orderId
      });
      alert(response.data.message || 'Payment status updated successfully');
      fetchTransactions();
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating payment status');
    }
  };

  const getStatusColor = (description) => {
    if (description === 'Initial coins') {
      return 'primary';
    } else if (description === 'Price plan purchase') {
      return 'info';
    }
    return 'default';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Wallet Transactions</Typography>
        {/* <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Add Transaction
        </Button> */}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
            <Typography variant="h6">Today's Credit</Typography>
            <Typography variant="h4">{summary.today_credit.toFixed(2)}</Typography>
            <Typography variant="caption">Coins</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'white' }}>
            <Typography variant="h6">Today's Used</Typography>
            <Typography variant="h4">{summary.today_used.toFixed(2)}</Typography>
            <Typography variant="caption">Coins</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'white' }}>
            <Typography variant="h6">Total Credit</Typography>
            <Typography variant="h4">{summary.total_credit.toFixed(2)}</Typography>
            <Typography variant="caption">Coins (Filtered)</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, bgcolor: 'warning.light', color: 'white' }}>
            <Typography variant="h6">Total Debit</Typography>
            <Typography variant="h4">{summary.total_debit.toFixed(2)}</Typography>
            <Typography variant="caption">Coins (Filtered)</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          {/* <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Trainer</InputLabel>
              <Select
                value={filterTrainer}
                onChange={(e) => setFilterTrainer(e.target.value)}
                label="Trainer"
              >
                <MenuItem value="">All Trainers</MenuItem>
                {trainers.map((trainer) => (
                  <MenuItem key={trainer.id} value={trainer.id}>
                    {trainer.name} ({trainer.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Mobile Number"
              value={filterMobile}
              onChange={(e) => setFilterMobile(e.target.value)}
              placeholder="Search by mobile"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={filterDeviceType}
                onChange={(e) => {
                  setFilterDeviceType(e.target.value);
                  setFilterPricePlan('');
                  setFilterOrderId('');
                }}
                label="Device Type"
                size="small"
              >
                <MenuItem value="">All Device Types</MenuItem>
                <MenuItem value="android">Android</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Price Plan</InputLabel>
              <Select
                value={filterPricePlan}
                onChange={(e) => {
                  setFilterPricePlan(e.target.value);
                  setFilterOrderId('');
                }}
                label="Price Plan"
                size="small"
              >
                <MenuItem value="">All Plans</MenuItem>
                {pricePlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {`#${plan.id} - ${plan.amount} (${plan.coin_deduction} coins)`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Purchase List</InputLabel>
              <Select
                value={filterOrderId}
                onChange={(e) => setFilterOrderId(e.target.value)}
                label="Purchase List"
                size="small"
                disabled={!filterPricePlan}
              >
                <MenuItem value="">All Purchases</MenuItem>
                {purchaseOrderOptions.map((orderId) => (
                  <MenuItem key={orderId} value={orderId}>
                    {orderId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterTransactionType}
                onChange={(e) => setFilterTransactionType(e.target.value)}
                label="Type"
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear All Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        data={transactions}
        loading={loading}
        searchFields={['trainer_name', 'description', 'trainer_phone', 'order_id']}
        emptyMessage="No transactions found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'trainer', label: 'Trainer' },
          { field: 'mobile', label: 'Mobile' },
          { field: 'type', label: 'Type' },
          { field: 'amount', label: 'Amount' },
          { field: 'balance_after', label: 'Balance After' },
          { field: 'description', label: 'Description' },
          { field: 'order_id', label: 'Order ID' },
          { field: 'razorpay_status', label: 'Razorpay Status' },
          { field: 'status', label: 'Status' },
          { field: 'actions', label: 'Actions' },
          { field: 'date', label: 'Date' },
        ]}
        renderRow={(transaction) => (
          <TableRow key={transaction.id}>
            <TableCell>{transaction.id}</TableCell>
            <TableCell>
              {transaction.trainer_name || transaction.trainer_id}
            </TableCell>
            <TableCell>
              {transaction.trainer_phone || '-'}
            </TableCell>
            <TableCell>
              <Chip
                label={transaction.transaction_type}
                size="small"
                color={
                  transaction.transaction_type === 'credit'
                    ? 'success'
                    : 'error'
                }
              />
            </TableCell>
            <TableCell>{transaction.amount}</TableCell>
            <TableCell>{transaction.balance_after}</TableCell>
            <TableCell>
              {transaction.description ? (
                <Chip
                  label={transaction.description}
                  size="small"
                  color={getStatusColor(transaction.description)}
                />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              {transaction.order_id || '-'}
            </TableCell>
            <TableCell>
              {transaction.razorpay_status ? (
                <Chip
                  label={transaction.razorpay_status}
                  size="small"
                  color={
                    transaction.razorpay_status === 'success'
                      ? 'success'
                      : transaction.razorpay_status === 'pending'
                      ? 'warning'
                      : 'error'
                  }
                />
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={transaction.status}
                size="small"
                color={
                  transaction.status === 'success'
                    ? 'success'
                    : transaction.status === 'failed'
                    ? 'error'
                    : 'default'
                }
              />
            </TableCell>
            <TableCell>
              {transaction.razorpay_status === 'pending' && transaction.order_id && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleUpdateRazorpayStatus(transaction.order_id)}
                  title="Update to success and credit coins"
                >
                  <CheckCircleIcon />
                </IconButton>
              )}
            </TableCell>
            <TableCell>
              {new Date(transaction.created_at).toLocaleString()}
            </TableCell>
          </TableRow>
        )}
      />

      {/* Add Transaction Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Wallet Transaction</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControl fullWidth margin="normal">
            <InputLabel>Trainer</InputLabel>
            <Select
              value={formData.trainer_id}
              onChange={(e) =>
                setFormData({ ...formData, trainer_id: e.target.value })
              }
              label="Trainer"
              required
            >
              {trainers.map((trainer) => (
                <MenuItem key={trainer.id} value={trainer.id}>
                  {trainer.name} ({trainer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={formData.transaction_type}
              onChange={(e) =>
                setFormData({ ...formData, transaction_type: e.target.value })
              }
              label="Transaction Type"
            >
              <MenuItem value="credit">Credit</MenuItem>
              <MenuItem value="debit">Debit</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WalletTransactions;
